
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function GoodwoodPage() {
  useEffect(() => {
    document.title = 'Goodwood Painters Cape Town | Professional Painting Services | Interior Exterior Commercial';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Goodwood, Cape Town. Expert interior, exterior, and commercial painting contractors. Free quotes, quality workmanship, competitive prices.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Goodwood painters, Cape Town painting contractors, residential painting Goodwood');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/goodwood`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Goodwood Painters Cape Town",
      "description": "Professional painting services in Goodwood, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/goodwood`,
      "areaServed": {
        "@type": "Place",
        "name": "Goodwood, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.8847,
          "longitude": 18.5482
        },
        "geoRadius": "15000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Goodwood Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e58d3a8d4d5070df00afa7d8d625a6f5.jpg"
            alt="Professional painting services in Goodwood, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Goodwood Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Trusted painting contractors serving Goodwood and surrounding areas. Quality residential, commercial, and industrial painting services with over 15 years of local experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Painting Services in Goodwood
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From residential homes to commercial properties, we provide complete painting solutions tailored to Goodwood's diverse architectural landscape and community needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Residential Painting',
                description: 'Complete home painting services including interior walls, exterior facades, and specialty finishes for Goodwood family homes.',
                features: ['Interior & exterior painting', 'Color consultation', 'Surface preparation', 'Quality materials']
              },
              {
                icon: 'ri-building-line',
                title: 'Commercial Painting',
                description: 'Professional commercial painting for offices, retail spaces, and industrial facilities throughout Goodwood business district.',
                features: ['Office buildings', 'Retail spaces', 'Industrial facilities', 'Scheduled maintenance']
              },
              {
                icon: 'ri-community-line',
                title: 'Protective Coatings',
                description: 'Specialized protective coatings and waterproofing solutions designed for Goodwood\'s climate conditions.',
                features: ['Waterproofing', 'Anti-corrosion coatings', 'Weather protection', 'Long-lasting finishes']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Goodwood Residents Choose Our Painting Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Local Goodwood Expertise',
                    description: 'Deep understanding of local architecture, climate conditions, and community preferences in Goodwood area.'
                  },
                  {
                    title: 'Quality Workmanship',
                    description: 'Skilled craftsmen using premium materials and proven techniques for lasting, beautiful results.'
                  },
                  {
                    title: 'Competitive Pricing',
                    description: 'Fair, transparent pricing with detailed quotes and no hidden costs for all Goodwood painting projects.'
                  },
                  {
                    title: 'Reliable Service',
                    description: 'Punctual, professional service with clear communication throughout your painting project.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Goodwood%20residential%20home%2C%20team%20of%20skilled%20painters%20applying%20quality%20paint%2C%20modern%20suburban%20house%20exterior%20painting%2C%20professional%20painting%20equipment%20and%20tools%2C%20Cape%20Town%20residential%20area&width=600&height=700&seq=goodwood-team-work&orientation=portrait"
                alt="Professional painters working in Goodwood"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Goodwood Painting Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest completed projects in Goodwood, showcasing our commitment to quality and attention to detail.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Modern Family Home Renovation',
                location: 'Goodwood Heights',
                description: 'Complete interior and exterior painting transformation of a 4-bedroom family home.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20exterior%20painting%20completed%20in%20Goodwood%20Cape%20Town%2C%20beautiful%20residential%20house%20with%20fresh%20paint%2C%20contemporary%20suburban%20architecture%2C%20professional%20painting%20results&width=400&height=300&seq=goodwood-project-1&orientation=landscape'
              },
              {
                title: 'Commercial Office Building',
                location: 'Goodwood Business District',
                description: 'Professional commercial painting for a 3-story office building including common areas.',
                image: 'https://readdy.ai/api/search-image?query=Commercial%20office%20building%20painting%20project%20in%20Goodwood%2C%20professional%20business%20premises%20with%20fresh%20exterior%20paint%2C%20modern%20commercial%20architecture%2C%20quality%20commercial%20painting%20work&width=400&height=300&seq=goodwood-project-2&orientation=landscape'
              },
              {
                title: 'Heritage Home Restoration',
                location: 'Old Goodwood',
                description: 'Careful restoration painting of a heritage property maintaining historical authenticity.',
                image: 'https://readdy.ai/api/search-image?query=Heritage%20home%20restoration%20painting%20in%20Goodwood%20Cape%20Town%2C%20historical%20house%20with%20authentic%20period%20colors%2C%20traditional%20architecture%20painting%2C%20heritage%20property%20maintenance&width=400&height=300&seq=goodwood-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Goodwood Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Read testimonials from satisfied customers in Goodwood and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Mitchell',
                location: 'Goodwood Heights',
                rating: 5,
                text: 'Outstanding work on our family home. The team was professional, punctual, and delivered exactly what they promised. Our house looks amazing!'
              },
              {
                name: 'David Thompson',
                location: 'Goodwood Central',
                rating: 5,
                text: 'Excellent commercial painting service for our office building. Minimal disruption to business operations and fantastic results. Highly recommended.'
              },
              {
                name: 'Maria Santos',
                location: 'Goodwood East',
                rating: 5,
                text: 'The attention to detail was impressive. They took great care with our heritage home and the color consultation was invaluable. Perfect results!'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Transform Your Goodwood Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us today for a free consultation and detailed quote. Let our experienced team bring your vision to life with professional painting services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
